import { getFileUrl } from '@/clients/http'
import { CategoryModel } from '@/http/models/ProductCategory'
import { Image as ImageIcon } from 'lucide-react'
import { useForm } from 'react-hook-form'

import { useEffect, useState } from 'react'

import { useEnterSubmit } from '@/hooks/ui'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

import { SwitchInput, TextInput } from '@/components/atoms'
import { MediaGalleryModal } from '@/components/media/MediaGalleryModal'

import { ProductCategoryModel } from '@/types'

import { CategoryTreeSelect } from './CategoryTreeSelect'
import { CategoryFormState, categoryFieldsConfig } from './categoryFieldsConfig'
import { Spinner } from '@/components/ui/spinner'

interface CategoryDialogProps {
  open: boolean
  mode: 'create' | 'edit' | 'view'
  editCategory: ProductCategoryModel.ProductCategory | null
  closeDialog: () => void
  onCreate: (data: CategoryFormState) => void
  onUpdate: (id: string, data: CategoryFormState) => void
  loading?: boolean
}

export const CategoryDialog = ({
  open,
  mode,
  editCategory,
  closeDialog,
  onCreate,
  onUpdate,
  loading = false,
}: CategoryDialogProps) => {
  const {
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
    control,
  } = useForm<CategoryFormState>({
    defaultValues: {
      name: '',
      slug: '',
      description: '',
      status: 'active',
      parent_id: null,
      image_url_temp: '',
      image_id: '',
      meta_title: '',
      meta_keywords: '',
      meta_description: '',
    },
  })

  const [parentCategoryLabel, setParentCategoryLabel] = useState('')
  const [galleryOpen, setGalleryOpen] = useState(false)
  const formValues = watch()
  const { fields } = categoryFieldsConfig({ formValues })

  const generateSlug = (name: string) => {
    return name.toLowerCase().replace(/\s+/g, '-')
  }

  useEffect(() => {
    if ((mode === 'edit' || mode === 'view') && editCategory) {
      reset({
        name: editCategory.name,
        slug: editCategory.slug,
        description: editCategory.description,
        status: editCategory.status,
        parent_id: editCategory.parent_id,
        image_url_temp: editCategory?.image?.path,
        image_id: editCategory?.image?._id,
        meta_title: editCategory?.meta_title ?? '',
        meta_keywords: editCategory?.meta_keywords ?? '',
        meta_description: editCategory?.meta_description ?? '',
      })

      if (editCategory.parent_id) {
        CategoryModel.getById({ params: { id: editCategory.parent_id } }).then((response) => {
          if (response.success && response.data.result) {
            setParentCategoryLabel(response.data.result.name)
          }
        })
      } else {
        setParentCategoryLabel('')
      }
    } else {
      reset({
        name: '',
        slug: '',
        description: '',
        status: 'active',
        parent_id: null,
        image_url_temp: '',
        image_id: '',
        meta_title: '',
        meta_keywords: '',
        meta_description: '',
      })
      setParentCategoryLabel('')
    }
  }, [mode, editCategory, reset])

  const onSubmit = async (data: CategoryFormState) => {
    if (!data.parent_id) {
      delete data.parent_id
    }
    if (!data.image_url_temp) {
      delete data.image_url_temp
    }
    if (mode === 'create') {
      onCreate(data)
    } else {
      if (!editCategory?._id) return
      onUpdate(editCategory._id, data)
    }
  }

  useEnterSubmit(handleSubmit(onSubmit), open && mode !== 'view')

  return (
    <Dialog open={open} onOpenChange={closeDialog}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create'
              ? 'Add category'
              : mode === 'edit'
                ? 'Edit category'
                : 'Category details'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'create'
              ? 'Create a new product category.'
              : mode === 'edit'
                ? 'Update category information.'
                : 'View category details.'}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* Left Column - Text Inputs */}
          <div className="space-y-4">
            <TextInput
              control={control}
              name="name"
              label="Name"
              placeholder="Electronics"
              disabled={mode === 'view'}
              rules={fields.name.rules}
              errors={errors}
              onChange={(e) => {
                if (mode === 'create') {
                  setValue('slug', generateSlug(e.target.value))
                }
              }}
            />

            <TextInput
              control={control}
              name="slug"
              label="Slug"
              placeholder="electronics"
              disabled={mode === 'view'}
              rules={fields.slug.rules}
              errors={errors}
            />

            <div className="space-y-1.5">
              <span className="text-sm text-ink">Parent category</span>
              <CategoryTreeSelect
                value={formValues.parent_id}
                onChange={(value, label) => {
                  setValue('parent_id', value)
                  setParentCategoryLabel(label)
                }}
                placeholder="Select parent category (optional)"
                defaultLabel={parentCategoryLabel}
                disabled={mode === 'view'}
                selectionMode="category"
                excludeId={editCategory?._id}
              />
            </div>

            <TextInput
              control={control}
              name="description"
              label="Description"
              placeholder="Category description"
              disabled={mode === 'view'}
              multiline
              rows={4}
              rules={fields.description.rules}
              errors={errors}
            />
          </div>

          {/* Right Column - Media & Status */}
          <div className="space-y-4">
            {/* Image Selection */}
            <div className="space-y-1.5">
              <span className="text-sm text-ink">Image</span>

              <div className="flex min-h-40 flex-col items-center justify-center rounded-lg border border-dashed border-border bg-subdued p-4">
                {formValues.image_url_temp ? (
                  <div className="group relative aspect-video w-full overflow-hidden rounded-md md:aspect-auto md:h-44">
                    <img
                      src={getFileUrl(formValues.image_url_temp)}
                      alt="Category"
                      className="w-full h-full object-cover"
                    />

                    <div className="absolute inset-0 flex items-center justify-center gap-2 bg-ink/50 opacity-0 transition-opacity group-hover:opacity-100">
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => setGalleryOpen(true)}
                      >
                        Change
                      </Button>
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={() => {
                          setValue('image_url_temp', '')
                          setValue('image_id', '')
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2 text-center">
                    <ImageIcon
                      className="mx-auto size-5 text-muted-foreground"
                      aria-hidden="true"
                    />
                    <p className="text-sm text-muted-foreground">No image selected</p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setGalleryOpen(true)}
                      disabled={mode === 'view'}
                    >
                      Select image
                    </Button>
                  </div>
                )}
              </div>
            </div>

            <SwitchInput
              control={control}
              name="status"
              label="Status"
              description={
                formValues.status === 'active' ? 'Category is visible' : 'Category is hidden'
              }
              disabled={mode === 'view'}
              activeValue="active"
              inactiveValue="inactive"
            />
          </div>
        </div>

        <div className="border-t border-border-subtle pt-4">
          <h3 className="mb-3 text-sm font-semibold text-ink">Search engine listing</h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <TextInput
              control={control}
              name="meta_title"
              label="Page title"
              placeholder="Page title shown in search results"
              disabled={mode === 'view'}
              rules={fields.meta_title.rules}
              errors={errors}
            />
            <TextInput
              control={control}
              name="meta_keywords"
              label="Keywords"
              placeholder="keyword1, keyword2, keyword3"
              disabled={mode === 'view'}
              rules={fields.meta_keywords.rules}
              errors={errors}
            />
            <div className="md:col-span-2">
              <TextInput
                control={control}
                name="meta_description"
                label="Meta description"
                placeholder="Description shown in search results"
                disabled={mode === 'view'}
                multiline
                rows={3}
                rules={fields.meta_description.rules}
                errors={errors}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={closeDialog} disabled={loading}>
            {mode === 'view' ? 'Close' : 'Cancel'}
          </Button>
          {mode !== 'view' && (
            <Button type="button" disabled={loading} onClick={handleSubmit(onSubmit)}>
              {loading && <Spinner />}
              {mode === 'create' ? 'Create' : 'Save'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>

      <MediaGalleryModal
        open={galleryOpen}
        onOpenChange={setGalleryOpen}
        onSelect={(files) => {
          if (files.length > 0) {
            setValue('image_url_temp', files[0].url)
            setValue('image_id', files[0].id)
          }
        }}
      />
    </Dialog>
  )
}
